/**
 * Organizations Tab Component
 * Displays organization entities with CRUD operations
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Hospital, Plus, Edit, Trash2, MapPin, Mail } from "lucide-react";

export function OrganizationsTab({ 
  organizations, 
  onAdd, 
  onEdit, 
  onDelete 
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-slate-400">Manage organization entities for clinical site assignment</p>
        <Button
          onClick={() => onAdd()}
          className="bg-violet-500 text-white hover:bg-violet-400"
          data-testid="add-organization-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Organization
        </Button>
      </div>
      
      {organizations.length === 0 ? (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="py-12 text-center">
            <Hospital className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No organizations yet</p>
            <p className="text-slate-500 text-sm mt-1">Add your first organization to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.map((org) => (
            <Card 
              key={org.id} 
              className="bg-[#151A23] border-slate-700 hover:border-violet-500/50 transition-colors" 
              data-testid={`organization-card-${org.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                      <Hospital className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">{org.name}</CardTitle>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(org)}
                      className="text-slate-400 hover:text-violet-400"
                      data-testid={`edit-organization-${org.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(org.id)}
                      className="text-slate-400 hover:text-red-400"
                      data-testid={`delete-organization-${org.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {org.address && (
                  <div className="flex items-start gap-2 text-sm text-slate-400 mb-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{org.address}, {org.city}, {org.state} {org.zip_code}</span>
                  </div>
                )}
                {org.contact_email && (
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{org.contact_email}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-700">
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-violet-400">{org.user_count || 0}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-white">{org.site_count || 0}</p>
                    <p className="text-xs text-slate-500">Sites</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
